import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { RawTransactionDto } from 'api/Transaction/Types/RawTransactionDto'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { Spinner } from 'components/Spinner'
import { createGridState } from 'features/DataGrid/helpers'
import { GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, BuildQueryProps } from 'utils/QueryHelpers'

import transactionOverviewWalletOrClientColumn from './Details/TransactionsOverviewForWalletOrClient/transactionOverviewWalletOrClientColumn'

export enum TransactionFilterType {
  Client,
  Wallet,
}

interface Props {
  clientId?: string
  walletId?: string
  filterBy: TransactionFilterType
  getTransaction(transaction: TransactionDto): void
  query: BuildQueryProps
}

const SelectTransaction = (props: Props): JSX.Element => {
  const [isLoading, setLoading] = useState<boolean>(true)
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [transactions, setTransactions] = useState<RawTransactionDto[]>([])

  const fetchTransactionsFactory = useCallback(
    async (query?: PageQuery) => {
      if (TransactionFilterType.Client === props.filterBy && props?.clientId) {
        return await walletsApiClient.getWalletTransactionsByClientId(props.clientId, {
          ...query,
          search: buildQuery(props.query),
        })
      }
      if (TransactionFilterType.Wallet === props.filterBy && props?.walletId) {
        return await walletsApiClient.getWalletTransactions(props.walletId, {
          ...query,
          search: buildQuery(props.query),
        })
      }
    },
    [props] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    ;(async () => {
      await fetchGrid(async (): Promise<PageData<RawTransactionDto[]> | undefined> => {
        const paginationData = await fetchTransactionsFactory()
        if (paginationData) {
          setTransactions(paginationData.items)
        }
        return paginationData
      })
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (callback: () => Promise<PageData<RawTransactionDto[]> | undefined>) => {
    setLoading(true)

    const gridState = createGridState(getPaginationInitialState(), gridFilterState)
    const response = await callback()
    if (response?.totalResults) {
      const total = response.totalResults

      setGridFilterState({
        ...gridState,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skip: gridState.skip,
        total,
      })
    }

    setLoading(false)
  }

  const handlePageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip,
      recordsPerPage: take,
    })

    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
    })

    const paginationData = await fetchTransactionsFactory({
      pageIndex,
      pageSize,
    })
    if (paginationData) {
      const total = paginationData.totalResults

      setTransactions(paginationData.items)
      setGridFilterState({
        ...gridFilterState,
        take,
        skip,
        pageIndex,
        total,
      })
    }
  }

  const handleSelect = (dataItem: TransactionDto) => {
    props.getTransaction(dataItem)
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <div style={{ margin: 16 }}>
      <Grid
        resizable
        selectedField='selected'
        data={transactions}
        {...gridFilterState}
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        total={gridFilterState.total}
        take={gridFilterState.take}
        skip={gridFilterState.skip}
        onPageChange={handlePageChange}
        pageable={pageableSettings}
        style={{ maxHeight: '70vh' }}
      >
        <Column
          field='Select'
          width={130}
          cell={(gridCellProps: GridCellProps) => (
            <td data-test='select-column'>
              <Button
                onClick={() => handleSelect(gridCellProps.dataItem)}
                data-test='select-button'
              >
                Select
              </Button>
            </td>
          )}
        />
        {transactionOverviewWalletOrClientColumn
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
      </Grid>
    </div>
  )
}

export default SelectTransaction
